import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, CardBody, Spinner, cn } from "@heroui/react";
import { ChevronLeft, ArrowRight, Copy, Check, RefreshCw, AlertCircle, CheckCircle, ExternalLink, HelpCircle, Smartphone } from "lucide-react";
import { useAuth } from "@src/presentation/contexts/AuthContext";
import { useGenerateWhatsAppCode } from "@src/presentation/hooks/users";

const WHATSAPP_NUMBER = '233542480050';

/**
 * WhatsApp Icon component
 */
const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
);

/**
 * WhatsApp Link Page
 * 
 * A dedicated page for users to link their WhatsApp account.
 * Particularly useful for:
 * - Users redirected from WhatsApp
 * - Users who clicked a link-whatsapp deep link
 * - Mobile users who need a full-screen experience
 * 
 * The page automatically generates a code on load and provides
 * a streamlined UX for copying and sending the code to WhatsApp.
 */
export default function WhatsAppLinkPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, user } = useAuth();
    const [copied, setCopied] = useState(false);
    const [expirySeconds, setExpirySeconds] = useState<number | null>(null);

    // Check if user came from WhatsApp redirect
    const fromWhatsApp = searchParams.get('source') === 'whatsapp';

    const {
        mutate: generateCode,
        data,
        isPending,
        isError,
        error,
        reset,
    } = useGenerateWhatsAppCode();

    // Generate code on mount if authenticated and not already linked
    useEffect(() => {
        if (isAuthenticated && !user?.whatsappLinked) {
            generateCode();
        }
    }, [isAuthenticated, user?.whatsappLinked, generateCode]);

    // Set initial expiry seconds when data arrives
    useEffect(() => {
        if (data?.expiresInSeconds) {
            setExpirySeconds(data.expiresInSeconds);
        }
    }, [data?.expiresInSeconds]);

    // Countdown timer
    useEffect(() => {
        if (expirySeconds === null || expirySeconds <= 0) return;

        const timer = setInterval(() => {
            setExpirySeconds(prev => {
                if (prev === null || prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [expirySeconds]);

    const handleCopyCode = async () => {
        if (!data?.linkCode) return;

        try {
            await navigator.clipboard.writeText(data.linkCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy code:', err);
        }
    };

    const handleOpenWhatsApp = () => {
        const message = data?.linkCode ? encodeURIComponent(data.linkCode) : '';
        const url = `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${message}` : ''}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleRegenerateCode = () => {
        setCopied(false);
        setExpirySeconds(null);
        reset();
        generateCode();
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isExpired = expirySeconds !== null && expirySeconds <= 0;

    // If user is already linked, show success state
    if (isAuthenticated && user?.whatsappLinked) {
        return (
            <div className="h-full overflow-auto bg-background">
                {/* Header */}
                <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex h-14 items-center gap-3">
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <h1 className="text-lg font-semibold text-foreground">Link WhatsApp</h1>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="max-w-md mx-auto">
                        <Card className="border border-default-200 rounded shadow-none">
                            <CardBody className="p-6">
                                <div className="flex flex-col items-center text-center gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366]/10">
                                        <CheckCircle className="h-7 w-7 text-[#25D366]" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-foreground">
                                            WhatsApp Connected
                                        </h2>
                                        <p className="mt-2 text-sm text-default-500">
                                            Your WhatsApp account is already connected to PascoHub. You can access PASCOs directly from WhatsApp.
                                        </p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 w-full mt-2">
                                        <Button
                                            onPress={handleOpenWhatsApp}
                                            className="flex-1 rounded bg-[#25D366] hover:bg-[#20BA5A] text-white"
                                            startContent={<WhatsAppIcon className="h-4 w-4" />}
                                        >
                                            Open WhatsApp
                                        </Button>
                                        <Button
                                            variant="flat"
                                            onPress={() => navigate('/')}
                                            className="flex-1 rounded"
                                        >
                                            Go Home
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </main>
            </div>
        );
    }

    // If not authenticated, prompt to login
    if (!isAuthenticated) {
        return (
            <div className="h-full overflow-auto bg-background">
                {/* Header */}
                <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex h-14 items-center gap-3">
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <h1 className="text-lg font-semibold text-foreground">Link WhatsApp</h1>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="max-w-md mx-auto">
                        <Card className="border border-default-200 rounded shadow-none">
                            <CardBody className="p-6">
                                <div className="flex flex-col items-center text-center gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366]/10">
                                        <WhatsAppIcon className="h-7 w-7 text-[#25D366]" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-foreground">
                                            Link WhatsApp to PascoHub
                                        </h2>
                                        <p className="mt-2 text-sm text-default-500">
                                            Sign in to generate your WhatsApp linking code and access PASCOs directly from WhatsApp.
                                        </p>
                                    </div>
                                    <Button
                                        onPress={() => navigate('/auth', { state: { from: '/link-whatsapp' } })}
                                        color="primary"
                                        className="w-full rounded mt-2"
                                        endContent={<ArrowRight className="h-4 w-4" />}
                                    >
                                        Sign In to Continue
                                    </Button>
                                    {fromWhatsApp && (
                                        <p className="text-xs text-default-400 mt-2">
                                            You'll be redirected back here after signing in.
                                        </p>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="h-full overflow-auto bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-14 items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <div className="flex items-center gap-2">
                                <Smartphone className="h-4 w-4 text-[#25D366]" />
                                <h1 className="text-lg font-semibold text-foreground">Link WhatsApp</h1>
                            </div>
                        </div>
                        <Button
                            variant="light"
                            size="sm"
                            onPress={() => navigate('/help')}
                            startContent={<HelpCircle className="h-4 w-4" />}
                            className="rounded text-default-500"
                        >
                            <span className="hidden sm:inline">Help</span>
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="max-w-lg mx-auto space-y-6">
                    {/* Hero Section */}
                    <div className="text-center space-y-3 py-2">
                        <div className="flex justify-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366]/10">
                                <WhatsAppIcon className="h-6 w-6 text-[#25D366]" />
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-foreground">
                            Link Your WhatsApp
                        </h2>
                        <p className="text-sm text-default-500 max-w-md mx-auto">
                            {fromWhatsApp
                                ? "Complete the linking process to access PASCOs directly in WhatsApp."
                                : "Connect your WhatsApp to search and access PASCOs without leaving your chat."}
                        </p>
                    </div>

                    {/* Code Card */}
                    <Card className="border border-default-200 rounded shadow-none">
                        <CardBody className="p-4 sm:p-6">
                            {isPending ? (
                                <div className="flex flex-col items-center justify-center py-8 gap-3">
                                    <div className="relative">
                                        <div className="absolute inset-0 rounded-full bg-[#25D366]/20 animate-ping" />
                                        <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366]/10">
                                            <Spinner size="sm" color="success" />
                                        </div>
                                    </div>
                                    <p className="text-sm text-default-500">Generating your code...</p>
                                </div>
                            ) : isError ? (
                                <div className="flex flex-col items-center gap-4 py-4">
                                    <div className="flex items-center gap-2 p-3 rounded bg-danger-50 dark:bg-danger-900/20 w-full">
                                        <AlertCircle className="h-4 w-4 text-danger flex-shrink-0" />
                                        <p className="text-sm text-danger">
                                            {(error as Error)?.message || 'Failed to generate code. Please try again.'}
                                        </p>
                                    </div>
                                    <Button
                                        onPress={handleRegenerateCode}
                                        className="rounded"
                                        startContent={<RefreshCw className="h-4 w-4" />}
                                    >
                                        Try Again
                                    </Button>
                                </div>
                            ) : data ? (
                                <div className="flex flex-col gap-5">
                                    {/* Code Display */}
                                    <div className={cn(
                                        "flex flex-col items-center gap-3 py-5 px-4 rounded border-2 border-dashed transition-colors",
                                        isExpired
                                            ? "bg-danger-50/50 dark:bg-danger-900/10 border-danger-200"
                                            : "bg-[#25D366]/5 border-[#25D366]/30"
                                    )}>
                                        <p className="text-xs text-default-400 uppercase tracking-wider font-medium">
                                            Your Linking Code
                                        </p>
                                        <div className={cn(
                                            "text-2xl sm:text-3xl font-mono font-bold tracking-[0.2em] select-all",
                                            isExpired ? "text-danger/50 line-through" : "text-foreground"
                                        )}>
                                            {data.linkCode}
                                        </div>
                                        {!isExpired && (
                                            <Button
                                                size="sm"
                                                variant="flat"
                                                color={copied ? "success" : "default"}
                                                onPress={handleCopyCode}
                                                className="rounded"
                                                startContent={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                            >
                                                {copied ? "Copied!" : "Copy Code"}
                                            </Button>
                                        )}
                                    </div>

                                    {/* Timer or Regenerate */}
                                    <div className="flex items-center justify-center">
                                        {isExpired ? (
                                            <Button
                                                onPress={handleRegenerateCode}
                                                color="danger"
                                                variant="flat"
                                                className="rounded"
                                                startContent={<RefreshCw className="h-4 w-4" />}
                                            >
                                                Code Expired · Generate New
                                            </Button>
                                        ) : expirySeconds !== null && (
                                            <div className="flex items-center gap-2 text-sm text-default-500">
                                                <span>⏱️</span>
                                                <span>Expires in <strong className="text-foreground">{formatTime(expirySeconds)}</strong></span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Steps */}
                                    {!isExpired && (
                                        <div className="border-t border-default-100 pt-5">
                                            <h3 className="text-sm font-semibold text-foreground mb-3">
                                                How to complete linking:
                                            </h3>
                                            <ol className="space-y-2.5">
                                                <li className="flex items-start gap-3">
                                                    <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold">1</span>
                                                    <span className="text-sm text-default-600">Copy the code above</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold">2</span>
                                                    <span className="text-sm text-default-600">Open WhatsApp and message Pepper Bot</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold">3</span>
                                                    <span className="text-sm text-default-600">Send the code — you're done!</span>
                                                </li>
                                            </ol>
                                        </div>
                                    )}

                                    {/* Primary CTA */}
                                    {!isExpired && (
                                        <Button
                                            onPress={handleOpenWhatsApp}
                                            className="w-full rounded bg-[#25D366] hover:bg-[#20BA5A] text-white font-medium"
                                            size="lg"
                                            startContent={<WhatsAppIcon className="h-5 w-5" />}
                                            endContent={<ExternalLink className="h-4 w-4" />}
                                        >
                                            Open WhatsApp
                                        </Button>
                                    )}

                                    {/* Privacy Note */}
                                    {!isExpired && (
                                        <p className="text-xs text-center text-default-400">
                                            🔒 Your WhatsApp number is only used to recognize you in chat.
                                            We never share your data.
                                        </p>
                                    )}
                                </div>
                            ) : null}
                        </CardBody>
                    </Card>

                    {/* Skip Option */}
                    <div className="text-center">
                        <Button
                            variant="light"
                            onPress={() => navigate('/')}
                            className="rounded text-default-500"
                        >
                            I'll do this later
                        </Button>
                    </div>
                </div>
            </main>
        </div>
    );
}
